/* eslint-disable*/
import React, { useEffect, useState, useRef, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSearch,
  faHome,
  faSync,
  faPlay,
  faPause
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import moment from "moment";
import { showToast } from "../../components/toast";
import '../../components/skeletonRow.css'

const CallSummaryReport = () => {
  const [callData, setCallData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const tableRenderedRef = useRef(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Separate state for form inputs and actual filters
  const [formInputs, setFormInputs] = useState({
    fullname: "",
    call_from: "",
    call_to: "",
    call_type: "",
    status: "",
    assignedto: "",
    created_on_start: null,
    created_on_end: null
  });

  const [filters, setFilters] = useState({
    fullname: "",
    call_from: "",
    call_to: "",
    call_type: "",
    status: "",
    assignedto: "",
    created_on_start: "",
    created_on_end: "",
    orderbycol: "created_on",
    orderby: "desc",
    page: 1,
    limit: 100
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    showingRecords: "0 to 0 of 0"
  });

  const [dropdownOptions, setDropdownOptions] = useState({
    callTypeList: [],
    statusList: [],
    userList: []
  });

  // Refs for double scroll
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

  // Fetch call summary data
  const fetchCallData = useCallback(async (pageNumber = 1) => {
    try {
      setLoading(true);
      setIsRendering(true);
      tableRenderedRef.current = false;

      const token = localStorage.getItem("authToken");

      const payload = {
        fullname: filters.fullname || "",
        call_from: filters.call_from || "",
        call_to: filters.call_to || "",
        call_type: filters.call_type || "",
        status: filters.status || "",
        assignedto: filters.assignedto || "",
        created_on_start: filters.created_on_start ? moment(filters.created_on_start).format("YYYY-MM-DD") : "",
        created_on_end: filters.created_on_end ? moment(filters.created_on_end).format("YYYY-MM-DD") : "",
        orderbycol: filters.orderbycol,
        orderby: filters.orderby,
        page: pageNumber - 1, // Backend expects 0-based page
        limit: filters.limit
      };

      console.log("API Payload:", payload); // Debug log

      // Remove empty values
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      const response = await axios.post(
        `${API_URL}report-call-summary`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*",
            "Content-Type": "application/json"
          }
        }
      );

      console.log("API Response:", response.data); // Debug log

      if (response.data) {
        const data = response.data;

        // Handle different response structures
        const callSummaryData = data.callsummary || data.data || [];
        setCallData(callSummaryData);

        const totalRecords = data.total || data.recordsTotal || callSummaryData.length;
        const totalPages = Math.ceil(totalRecords / filters.limit);
        const startRecord = ((pageNumber - 1) * filters.limit) + 1;
        const endRecord = Math.min(pageNumber * filters.limit, totalRecords);

        setPagination({
          currentPage: pageNumber,
          totalPages: totalPages,
          totalRecords: totalRecords,
          showingRecords: `${startRecord} to ${endRecord} of ${totalRecords}`
        });

        // Set dropdown options from response
        if (data.calltypelist || data.statuslist || data.userlist) {
          setDropdownOptions({
            callTypeList: data.calltypelist || [],
            statusList: data.statuslist || [],
            userList: data.userlist || []
          });
        } else {
          // If no dropdown data in response, fetch it separately
          fetchDropdownData();
        }

        setTimeout(() => {
          tableRenderedRef.current = true;
          checkRenderingStatus();
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching call summary data:", error);
      console.error("Error details:", error.response?.data); // Debug log
      setCallData([]);
      tableRenderedRef.current = true;
      checkRenderingStatus();
      showToast.error("Failed to load call summary data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const checkRenderingStatus = () => {
    if (tableRenderedRef.current) {
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    }
  };

  // Fetch dropdown data separately
  const fetchDropdownData = async () => {
    try {
      const token = localStorage.getItem("authToken");

      // Try different endpoints for dropdown data
      const endpoints = [
        `${API_URL}report-call-summary/filters`,
        `${API_URL}report-call-summary`,
        `${API_URL}dropdown/call-summary-filters`
      ];

      let dropdownData = null;

      for (const endpoint of endpoints) {
        try {
          const response = await axios.post(
            endpoint,
            { page: 0, limit: 1 },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                accept: "*/*",
                "Content-Type": "application/json"
              }
            }
          );

          if (response.data && (response.data.calltypelist || response.data.statuslist || response.data.userlist)) {
            dropdownData = response.data;
            break;
          }
        } catch (error) {
          console.log(`Failed to fetch from ${endpoint}:`, error.message);
          continue;
        }
      }

      if (dropdownData) {
        setDropdownOptions({
          callTypeList: dropdownData.calltypelist || [
            { value: "0", label: "outgoing" },
            { value: "1", label: "incoming" }
          ],
          statusList: dropdownData.statuslist || [
            { pick_name: "queued" },
            { pick_name: "in-progress" },
            { pick_name: "completed" },
            { pick_name: "failed" },
            { pick_name: "busy" },
            { pick_name: "no-answer" }
          ],
          userList: dropdownData.userlist || []
        });
      } else {
        // Fallback to default values if API fails
        setDropdownOptions({
          callTypeList: [
            { value: "0", label: "outgoing" },
            { value: "1", label: "incoming" }
          ],
          statusList: [
            { pick_name: "queued" },
            { pick_name: "in-progress" },
            { pick_name: "completed" },
            { pick_name: "failed" },
            { pick_name: "busy" },
            { pick_name: "no-answer" }
          ],
          userList: []
        });
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      // Set default values
      setDropdownOptions({
        callTypeList: [
          { value: "0", label: "outgoing" },
          { value: "1", label: "incoming" }
        ],
        statusList: [
          { pick_name: "queued" },
          { pick_name: "in-progress" },
          { pick_name: "completed" },
          { pick_name: "failed" },
          { pick_name: "busy" },
          { pick_name: "no-answer" }
        ],
        userList: []
      });
    }
  };

  useEffect(() => {
    fetchCallData(filters.page);
  }, [filters, fetchCallData]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Double scroll implementation
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };

    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [callData]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  // Handle form input changes (without applying filters immediately)
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle dropdown changes with immediate filter application
  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    setFormInputs(prev => ({
      ...prev,
      [name]: value
    }));

    // Apply filters immediately for dropdown changes
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  // Handle date range changes for Created Date
  const handleCreatedDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs(prev => ({
      ...prev,
      created_on_start: start,
      created_on_end: end
    }));
  };

  // Apply all filters (called on Enter key or search button click)
  const applyFilters = () => {
    setFilters(prev => ({
      ...prev,
      fullname: formInputs.fullname,
      call_from: formInputs.call_from,
      call_to: formInputs.call_to,
      call_type: formInputs.call_type,
      status: formInputs.status,
      assignedto: formInputs.assignedto,
      created_on_start: formInputs.created_on_start,
      created_on_end: formInputs.created_on_end,
      page: 1
    }));
  };

  // Handle Enter key in text inputs
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const handleSort = (column) => {
    setFilters(prev => ({
      ...prev,
      orderbycol: column,
      orderby: prev.orderbycol === column && prev.orderby === "asc" ? "desc" : "asc",
      page: 1
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  // Audio playback functionality
  const handleAudioPlay = (index, audioElement) => {
    if (currentlyPlaying === index) {
      // Pause current audio
      audioElement.pause();
      audioElement.currentTime = 0;
      setCurrentlyPlaying(null);
    } else {
      // Stop any currently playing audio
      if (currentlyPlaying !== null) {
        const previousAudio = document.querySelector(`audio[data-index="${currentlyPlaying}"]`);
        if (previousAudio) {
          previousAudio.pause();
          previousAudio.currentTime = 0;
        }
      }

      // Play new audio
      audioElement.play();
      setCurrentlyPlaying(index);

      // Handle audio end
      audioElement.onended = () => {
        setCurrentlyPlaying(null);
      };
    }
  };

  const exportReport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("authToken");

      const payload = {
        fullname: filters.fullname || "",
        call_from: filters.call_from || "",
        call_to: filters.call_to || "",
        call_type: filters.call_type || "",
        status: filters.status || "",
        assignedto: filters.assignedto || "",
        created_on_start: filters.created_on_start ? moment(filters.created_on_start).format("YYYY-MM-DD") : "",
        created_on_end: filters.created_on_end ? moment(filters.created_on_end).format("YYYY-MM-DD") : "",
        orderbycol: filters.orderbycol,
        orderby: filters.orderby
      };

      // Remove empty values
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      console.log("Export Payload:", payload); // Debug log

      const response = await axios.post(
        `${API_URL}report-call-summary/export`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `call-summary-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success("Report exported successfully.");
    } catch (error) {
      console.error("Error exporting report:", error);
      showToast.error("Failed to export report. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const clearFilters = () => {
    setFormInputs({
      fullname: "",
      call_from: "",
      call_to: "",
      call_type: "",
      status: "",
      assignedto: "",
      created_on_start: null,
      created_on_end: null
    });
    setFilters({
      fullname: "",
      call_from: "",
      call_to: "",
      call_type: "",
      status: "",
      assignedto: "",
      created_on_start: "",
      created_on_end: "",
      orderbycol: "created_on",
      orderby: "desc",
      page: 1,
      limit: 100
    });
  };

  useEffect(() => {
    const handleClearFilters = () => {
      clearFilters();
    };

    window.addEventListener('clearCallSummaryReportFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearCallSummaryReportFiltersEvent', handleClearFilters);
    };
  }, []);

  // Helper functions
  const getSortIcon = (column) => {
    if (filters.orderbycol !== column) return faSort;
    return filters.orderby === "asc" ? faSortUp : faSortDown;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "----";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "----";
      return moment(date).format("DD-MM-YYYY");
    } catch (error) {
      return "----";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "----";
    try {
      const time = new Date(timeString);
      if (isNaN(time.getTime())) return "----";
      return moment(time).format("HH:mm:ss");
    } catch (error) {
      return "----";
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "00:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getCallTypeText = (callType) => {
    return callType === '0' ? 'outgoing' : 'incoming';
  };

  const hasAnyFilter = () => {
    return (
      filters.fullname ||
      filters.call_from ||
      filters.call_to ||
      filters.call_type ||
      filters.status ||
      filters.assignedto ||
      filters.created_on_start ||
      filters.created_on_end ||
      filters.orderbycol !== "created_on" ||
      filters.orderby !== "desc"
    );
  };

  const isPrevDisabled = pagination.currentPage <= 1;
  const isNextDisabled = pagination.currentPage >= pagination.totalPages;

  return (
    <div className="panel-content mt-5">
      {/* {(loading || isRendering || exportLoading) && <LoadingSpinner />} */}

      {/* Header with Breadcrumb and Controls */}
      <div className="panel-heading d-flex justify-content-between">
        <div className="col-md-4">
          <div style={{ float: "left", position: "relative", marginLeft: "5px" }}>
            {hasAnyFilter() && (
              <button
                className="btn btn-default btn-xs ml-1"
                style={{ marginLeft: "6px" }}
                onClick={clearFilters}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
        <div>
          <span style={{ fontSize: "0.8rem" }} className="me-3">
            {loading ? (
              <span
                className="skeleton-text"
                style={{ width: "120px", display: "inline-block" }}
              ></span>
            ) : (
              pagination.showingRecords
            )}
          </span>

          <button
            className="btn btn-default btn-sm mx-1"
            onClick={() => fetchCallData(1)}
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={exportReport}
            disabled={exportLoading || callData.length === 0}
          >
            {exportLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Exporting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} /> Export Report
              </>
            )}
          </button>

          <button
            className={`btn btn-default btn-sm ${filters.page === 1 ? "d-none" : ""}`}
            title="Previous"
            onClick={() => handlePageChange(filters.page - 1)}
            disabled={filters.page === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          &nbsp;
          <button
            className={`btn btn-default btn-sm ${filters.page === pagination.totalPages ? "d-none" : ""}`}
            title="Next"
            onClick={() => handlePageChange(filters.page + 1)}
            disabled={filters.page === pagination.totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* Main Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="large-table-container-3">
          {/* Top Scrollbar */}
          <div
            ref={topScrollbarRef}
            onScroll={handleTopScroll}
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              height: "16px",
              marginBottom: "2px",
              background: "#f8f9fa",
            }}
          >
            <div
              ref={topScrollbarContentRef}
              style={{ height: "1px" }}
            ></div>
          </div>

          {/* Table Container */}
          <div
            ref={tableContainerRef}
            onScroll={syncScroll}
            style={{
              overflowY: "auto",
              height: "calc(112vh - 250px)",
              overflowX: "auto",
              position: "relative",
            }}
          >
            <table className="table table-condensed table-hover bg-white sticky-table">
              <thead>
                {/* Header Row */}
                <tr style={{ cursor: 'pointer' }}>
                  <th onClick={() => handleSort('subscriberfullname')}>
                    Name <FontAwesomeIcon icon={getSortIcon('subscriberfullname')} />
                  </th>
                  <th onClick={() => handleSort('call_from')}>
                    From <FontAwesomeIcon icon={getSortIcon('call_from')} />
                  </th>
                  <th onClick={() => handleSort('call_to')}>
                    To <FontAwesomeIcon icon={getSortIcon('call_to')} />
                  </th>
                  <th onClick={() => handleSort('call_type')}>
                    Call Type <FontAwesomeIcon icon={getSortIcon('call_type')} />
                  </th>
                  <th onClick={() => handleSort('status')}>
                    Status <FontAwesomeIcon icon={getSortIcon('status')} />
                  </th>
                  <th onClick={() => handleSort('assigned_user')}>
                    Assigned To <FontAwesomeIcon icon={getSortIcon('assigned_user')} />
                  </th>
                  <th onClick={() => handleSort('created_on')}>
                    Created Date <FontAwesomeIcon icon={getSortIcon('created_on')} />
                  </th>
                  <th>Duration</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Recording</th>
                </tr>

                {/* Filter Row */}
                <tr>
                  <th>
                    <input
                      type="text"
                      name="fullname"
                      placeholder="Search name..."
                      value={formInputs.fullname}
                      onChange={handleFormInputChange}
                      onKeyDown={handleKeyDown}
                      style={{
                        width: "120px",
                        padding: "5px",
                      }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="call_from"
                      placeholder="From..."
                      value={formInputs.call_from}
                      onChange={handleFormInputChange}
                      onKeyDown={handleKeyDown}
                      style={{
                        width: "120px",
                        padding: "5px",
                      }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="call_to"
                      placeholder="To..."
                      value={formInputs.call_to}
                      onChange={handleFormInputChange}
                      onKeyDown={handleKeyDown}
                      style={{
                        width: "120px",
                        padding: "5px",
                      }}
                    />
                  </th>
                  <th>
                    <select
                      name="call_type"
                      value={formInputs.call_type}
                      onChange={handleDropdownChange}
                      style={{ width: "120px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.callTypeList && dropdownOptions.callTypeList.map((item, index) => (
                        <option key={index} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <select
                      name="status"
                      value={formInputs.status}
                      onChange={handleDropdownChange}
                      style={{ width: "120px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.statusList && dropdownOptions.statusList.map((item, index) => (
                        <option key={index} value={item.pick_name}>
                          {item.pick_name}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <select
                      name="assignedto"
                      value={formInputs.assignedto}
                      onChange={handleDropdownChange}
                      style={{ width: "120px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.userList && dropdownOptions.userList.map((user, index) => (
                        <option key={index} value={user.id}>
                          {user.user_fullname}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <DatePicker
                        selected={formInputs.created_on_start}
                        onChange={handleCreatedDateChange}
                        startDate={formInputs.created_on_start}
                        endDate={formInputs.created_on_end}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText=""
                        monthsShown={2}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                        minDate={new Date(2018, 0, 1)}
                        maxDate={new Date(2030, 11, 31)}
                        dateFormat="dd-MM-yyyy"
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={applyFilters}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                </tr>
              </thead>
              <tbody>
                {(loading || isRendering) ? (
                  // Skeleton loading rows (initial load + filter apply)
                  Array.from({ length: 10 }).map((_, skeletonIndex) => (
                    <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                      <td><div className="skeleton-text" style={{ width: "90%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "50%", margin: "0 auto" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "50%", margin: "0 auto" }}></div></td>
                    </tr>
                  ))
                ) : callData.length > 0 ? (
                  callData.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <a
                          href={`/subscriber/edit/${item.subscriberid || item.id}`}
                          target="_blank"
                          style={{ textDecoration: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.subscriberfullname || item.fullname || 'N/A'}
                        </a>
                      </td>
                      <td>{item.call_from || 'N/A'}</td>
                      <td>{item.call_to || 'N/A'}</td>
                      <td>{getCallTypeText(item.call_type)}</td>
                      <td>{item.status || 'N/A'}</td>
                      <td>{item.assigned_user || 'N/A'}</td>
                      <td>{formatDate(item.created_on)}</td>
                      <td style={{ textAlign: "center" }}>{formatDuration(item.duration)}</td>
                      <td>{formatTime(item.start_time)}</td>
                      <td>{item.end_time ? formatTime(item.end_time) : '----'}</td>
                      <td style={{ textAlign: "center" }}>
                        {item.recording_url ? (
                          <>
                            <button
                              className="btn btn-link btn-sm play_audio"
                              onClick={(e) => {
                                e.preventDefault();
                                const audioElement = document.querySelector(`audio[data-index="${idx}"]`);
                                if (audioElement) handleAudioPlay(idx, audioElement);
                              }}
                              style={{ padding: '2px', color: '#007bff' }}
                            >
                              <FontAwesomeIcon
                                icon={currentlyPlaying === idx ? faPause : faPlay}
                              />
                            </button>
                            <audio data-index={idx} src={item.recording_url} style={{ display: 'none' }} />
                            <a
                              href={item.recording_url}
                              className="btn btn-link btn-sm"
                              style={{ marginLeft: "10px", padding: '2px', color: '#007bff' }}
                              download
                            >
                              <FontAwesomeIcon icon={faDownload} />
                            </a>
                          </>
                        ) : '----'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    {/* No records state kept unchanged */}
                  </tr>
                )}
              </tbody>

            </table>
          </div>
        </div>
      </div>

      <style jsx>{`
        .breadcrumb {
          background: transparent;
          margin-bottom: 0;
          padding: 0;
        }

        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
        }

        .panel {
          background: white;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .with-rounded-corner {
          border-radius: 4px;
        }

        .with-shadow {
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .table-condensed th,
        .table-condensed td {
          padding: 8px 5px;
          font-size: 12px;
        }

        .btn-default {
          background: #f8f9fa;
          border: 1px solid #ddd;
        }

        .btn-xs {
          padding: 1px 5px;
          font-size: 12px;
          line-height: 1.5;
        }

        .form-control.input-sm {
          height: 28px;
          padding: 3px 6px;
          font-size: 12px;
        }

        th {
          white-space: nowrap;
          background: #f8f9fa;
        }

        .table-hover tbody tr:hover {
          background-color: #f5f5f5;
        }

        .badge {
          font-size: 0.7em;
        }
      `}</style>
    </div>
  );
};

export default CallSummaryReport;